import { DataInterfaceComparer } from "../../Global/Base/DataSources/DataInterfaceComparer";
import { IDataSourceLoadInfo, IOrderBy } from "../../Global/Base/DataSources/Interfaces";
import { PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { BaseTreeViewGrid } from "./BaseTreeViewGrid";
import { Column } from "./DataGridSubcontrols/Columns/Column";
import { TreeHeaderRow } from "./DataGridSubcontrols/Components/TreeHeaderRow";
import { CollapseState, TreeViewDataRow } from "./DataGridSubcontrols/Components/TreeViewDataRow";
import { EmptyTreeDataGridMessage } from "./DataGridSubcontrols/DataGridMessages/EmptyTreeDataGridMessage";
import { EmptyDatagridMessageType, IEmptyDataGridMessage } from "./DataGridSubcontrols/DataGridMessages/IEmptyDataGridMessage";
import { ITreeViewRowData, TreeViewDataSource } from "./DataSource/TreeViewDataSource";
import { IEmptyMessageDataGrid } from "./IDataGrid";
import { IDataGridDataShownEventData } from "./IEvent";

export class TreeViewGrid extends BaseTreeViewGrid<TreeViewDataRow> implements IEmptyMessageDataGrid<TreeViewDataRow> {
    //#region Fields
    public emptyMessage: IEmptyDataGridMessage;
    private _dataSource: TreeViewDataSource;
    private _rootId: string;
    /** Počítadlo aktuálně běžících loaderů pro vykreslení dat */
    private _dataLoadCounter: number = 0;
    //#endregion Fields

    //#region Properties
    /**
     * Řádek hlavičky seznamu
     */
    public override headerRow: TreeHeaderRow;

    public override get dataSource(): TreeViewDataSource {
        return this._dataSource;
    }

    public override set dataSource(v: TreeViewDataSource) {
        this._dataSource = v;
        this._dataSource.dataLoaded.add((_sender, eventData) => {
            if (!this.visible)
                return;

            if (eventData.refreshedData)
                this._refreshRowsWithData(eventData);
            else {
                if (eventData.isRoot)
                    this._clearRows(true);
                this._showData(eventData);
            }

            this._areDataShowedDefferred.resolve();
            this._updateEmptyMessageVisibility(eventData.isFiltered);
            this.callEvent("DataShown", { data: eventData.dataGenerator } as IDataGridDataShownEventData);
        });

        this._dataSource.loadingStarted.add(() => {
            this._dataLoadCounter++;
            this._updateEmptyMessageVisibility();
        });

        this._dataSource.loadingCancelled.add((_sender, eventData) => {
            this._updateEmptyMessageVisibility(eventData.isFiltered);
        });

        this._dataSource.loadingEnded.add(async (_sender, eventData) => {
            this._dataLoadCounter--;
            if (eventData.emptyData === true) {
                this._updateEmptyMessageVisibility(eventData.isFiltered);
            }
        });
    }

    public get rootId(): string {
        return this._rootId;
    }

    public set rootId(v: string) {
        this._rootId = v;
        this.dataSource.rootId = v;
    }

    public override get columns(): Column[] {
        return this._columns;
    }

    public override set columns(v: Column[]) {
        this._columns = v;
        this.headerRow.render(this, this.columns, this.orderBy);
    }

    public override get orderBy(): IOrderBy[] {
        return this._orderBy;
    }

    public override set orderBy(value: IOrderBy[]) {
        if (DataInterfaceComparer.arraysEquals(value, this.orderBy)) // netřídit, pokud je nové třídění stejné jako předchozí
            return;

        if (value == null || value.length == 0) {
            this.headerRow.orderBy = null;
        } else {
            for (const ord of value) {
                for (const column of this.columns) { // prochází všechny sloupce
                    if (ord.attributeName == column.attrName) { // pokud se sloupec vyskytuje v poli atributů k setřízení
                        this.headerRow.orderBy = ord;
                        break;
                    }
                }
            }
        }

        this._orderBy = value;
        if (this.dataSource) {
            let oldDataKey: string = null;
            if (this._selectedDataRows.length > 0)
                oldDataKey = this._selectedDataRows[0].dataKey;

            this.dataSource.orderBy = this.orderBy;
            this.reloadData().then(() => {
                self.setTimeout(() => {
                    if (oldDataKey)
                        this.selectRow(oldDataKey);
                }, 0);
            });
        }
    }
    //#endregion Properties

    constructor(self?: any) {
        super(self);
    }

    protected override _constructor(): void {
        super._constructor();

        const listDiv = createElement({ classes: "p-list-div" });

        this.headerRow = new TreeHeaderRow();

        this.appendChild(this.headerRow);
        this.appendChild(listDiv);
        listDiv.appendChild(this._dataTable);

        this.emptyMessage = new EmptyTreeDataGridMessage();
        this.appendChild(this.emptyMessage);

        this.addEventListener("childDataRequest", (e: CustomEvent) => {
            if (e.target instanceof TreeViewDataRow) {
                this._areDataShowedDefferred = PromiseUtils.deferStateful<void>();
                this._loadChildData(e.target).then(() => e.detail.promiseResolvedFnc());
            }
        });

        this._dataRowFactory = () => new TreeViewDataRow();
    }

    //#region Methods
    /**
     * Zakáže možnost řazení kliknutím na hlavičku sloupce
     */
    public disableSort(): void {
        this.headerRow.disableSort = true;
    }

    public override async selectRow(dataKey: string): Promise<TreeViewDataRow> {
        if (dataKey === "-1") {
            // Rušíme výběr řádků
            this.clearSelectedRows();
            this._triggerSelectionChanged("-1");
            return null;
        }

        const element = this.dataRows.find(item => item.dataKey == dataKey);

        if (element) {
            let pElement = element;
            while (pElement.parentRow != null) {
                pElement.parentRow.state = CollapseState.expanded;
                pElement = pElement.parentRow;
            }
            element.isSelected = true;

            return element;
        } else {
            let parentHierarchy = await this.dataSource.getObjectHierarchy(dataKey);
            if (parentHierarchy == null)
                return null;
            const hierarchy: string[] = [];
            let exist: TreeViewDataRow = null;
            hierarchy.push(parentHierarchy.parentId);

            while (parentHierarchy != null && parentHierarchy.parentId != "0") {
                const previousParentId = parentHierarchy.parentId;
                parentHierarchy = await this.dataSource.getObjectHierarchy(parentHierarchy.parentId);

                if (parentHierarchy == null)
                    break;

                if (parentHierarchy.parentId == "0") {
                    exist = this.dataRows.find(item => item.dataKey == parentHierarchy.data.ID);
                    break;
                }

                exist = this.dataRows.find(item => item.dataKey == parentHierarchy.parentId);

                if (exist || parentHierarchy.parentId == previousParentId) {
                    break;
                }

                hierarchy.push(parentHierarchy.parentId);
            }
            if (!exist)
                return null;

            let lastRow = exist;
            for (const dkey of hierarchy.reverse()) {
                if (!lastRow)
                    break;
                lastRow.state = CollapseState.expanded;
                await this.dataShowedPromise;
                lastRow = lastRow.childRows.find(item => item.dataKey == dkey) || lastRow.childRows.find(item => item.dataKey == dataKey);
            }

            if (lastRow) {
                let row: TreeViewDataRow;
                if (lastRow.dataKey != dataKey) {
                    lastRow.state = CollapseState.expanded;
                    await this.dataShowedPromise;
                    row = this.dataRows.find(item => item.dataKey == dataKey);
                } else
                    row = lastRow;
                if (row) {
                    row.isSelected = true;
                    return row;
                }
                return lastRow;
            }
            return null;
        }
    }

    /**
     * Metoda pro odstranění všech řádků datagridu
     */
    public override clearDataRows(): void {
        this.dataRows.forEach(row => {
            if (row instanceof TreeViewDataRow)
                row.remove();
        });
        this.skippedRowsCount = 0;
    }

    /**
     * Stará se o zobrazení zprávy s informací, že nejsou dostupné žádné záznamy ke zobrazení
     * @param isFiltered Zda je aktivní filtr
     */
    protected _updateEmptyMessageVisibility(isFiltered: boolean = false): void {
        if (this.dataRows.length === 0) {
            this.classList.add("is-empty");
            this.emptyMessage.visible = true;

            if (this._dataLoadCounter > 0) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.dataLoading;
            } else if (isFiltered) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.filterEmpty;
            } else if (this.emptyMessage.messageType !== EmptyDatagridMessageType.queryModeEmpty) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.initialEmpty;
            }

            return;
        }

        this.classList.remove("is-empty");
        this.emptyMessage.visible = false;
    }

    /**
     * Spustí načtení dat do podřízené větve stromu
     * @param parent Prvek, pro který mají být načteny podřízené záznamy
     */
    protected override async _loadChildData(parent: TreeViewDataRow): Promise<void> {
        await this.dataSource.loadChildData(parent ? parent.dataKey : null);
    }

    /**
     * Vytvoří nové řádky s daty
     * @param eventData Informace o načtených datech
     */
    protected override _showData(eventData: IDataSourceLoadInfo): void {
        const data = eventData.dataGenerator;

        if (data.length === 0)
            this._updateEmptyMessageVisibility();

        let parent = null;

        for (const irow of (data() as Iterable<ITreeViewRowData>)) {
            const newRow = this._createRow(irow);
            newRow.render();

            if (irow.parentId == null) {
                this._dataTable.appendChild(newRow);
            } else {
                if (parent == null)
                    parent = this.dataRows.find(item => item.dataKey == irow.parentId);
                parent.appendChild(newRow);
            }
        }
    }

    private _clearRows(force: boolean = false): void {
        if (this._requestClearRows || force) {
            this.dataRows = [];
            this._dataTable.innerHTML = "";
            if (!force)
                this._requestClearRows = false;
        }
    }

    private _refreshRowsWithData(eventData: IDataSourceLoadInfo): void {
        this._clearRows(true);
        this._showData(eventData);
    }

    public async reloadData(): Promise<void> {
        this._requestClearRows = true;
        await this.dataSource.refreshData();
    }

    public loadData(): Promise<void> {
        this._requestClearRows = true;
        return this._loadChildData(null);
    }

    /**
     * Nastavuje grid do režimu QueryMode, kdy se nezobrazují žádná data dokud uživatel neprovede akci
     * @param dataLoad Funkce, která se má spustit po kliknutí na tlačítko pro zobrazení dat
     */
    public setToQueryMode(dataLoad: Function): void {
        this.emptyMessage.setTypeToQueryMode(dataLoad);

        this._updateEmptyMessageVisibility();
    }

    /** Ověřuje, zda je DataGrid v režimu QueryMode */
    public isInQueryMode(): boolean {
        return this.emptyMessage.messageType === EmptyDatagridMessageType.queryModeEmpty;
    }
    //#region Methods

    public override dispose(): void {
        super.dispose();
    }
}

registerCustomElement("tsc-TreeViewGrid", TreeViewGrid);
